import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuCircleAlert, LuCircleCheck } from "react-icons/lu";
import { axios } from "../../lib/axios";
import { formatPayroll } from "../../lib/currency";
import { todayIso } from "../../lib/week";
import { isEnglish, t } from "../../i18n";

const SHOWN = 5;

// One line per employee, worded from the numbers the server sends.
const DETAIL = {
  overdue_pay: (i) =>
    t("{days} يوم غير مسلَّم منذ {since}، بقيمة {amount}", {
      days: i.days,
      since: `\u2066${i.since}\u2069`, // keeps the date's digits in order inside Arabic text
      amount: formatPayroll(i.amount),
    }),
  problems: (i) =>
    [
      i.duplicate_days > 0 && t("يوم مسجّل مرتين ({n})", { n: i.duplicate_days }),
      i.no_wage_days > 0 && t("يوم بدون أجر ({n})", { n: i.no_wage_days }),
      i.bad_dates > 0 && t("تاريخ غير صحيح ({n})", { n: i.bad_dates }),
    ]
      .filter(Boolean)
      .join(isEnglish ? ", " : "، "),
  big_debt: (i) =>
    t("عليه {owed} ودخله في آخر 4 أسابيع {earned}", {
      owed: formatPayroll(i.owed),
      earned: formatPayroll(i.earned_4_weeks),
    }),
  maintenance_due: (i) => {
    const when = `\u2066${i.due}\u2069`;
    const what =
      i.kind === "next_due"
        ? t("حان موعد الصيانة القادمة ({problem}) بتاريخ {due}", { problem: i.problem, due: when })
        : t("صيانة مجدولة ({problem}) بتاريخ {due}", { problem: i.problem, due: when });
    return i.late ? `${what} — ${t("متأخرة")}` : what;
  },
  asset_reports: (i) =>
    `${i.message}${i.reporter ? ` — ${i.reporter}` : ""} (\u2066${i.reported_at}\u2069)`,
  no_work: (i) =>
    t("عمل {n} أيام الأسبوع الماضي ولا يوجد تسجيل هذا الأسبوع", { n: i.last_week_days }),
};

const TITLES = {
  overdue_pay: () => t("رواتب متأخرة لم تُسلَّم"),
  problems: () => t("بيانات تحتاج مراجعة"),
  big_debt: () => t("استقطاعات أكبر من دخل آخر 4 أسابيع"),
  no_work: () => t("لم يُسجَّل لهم عمل هذا الأسبوع"),
  maintenance_due: () => t("صيانة مستحقة"),
  asset_reports: () => t("بلاغات أعطال من رمز QR"),
};

const AlertCard = ({ alert }) => {
  const [all, setAll] = useState(false);
  const items = all ? alert.items : alert.items.slice(0, SHOWN);
  const hidden = alert.count - items.length;
  const describe = DETAIL[alert.type];
  return (
    <li className="card card-in p-4">
      <h3 className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">
        <LuCircleAlert aria-hidden="true" className="text-amber-600" />
        {TITLES[alert.type]()}
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-sm font-medium text-amber-800">
          {alert.count}
        </span>
      </h3>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((item) => (
          <li
            key={item.report_id ?? item.record_id ?? item.employee_id}
            className="flex flex-wrap gap-x-2"
          >
            <Link
              to={
                item.record_id || item.report_id ? "/maintenance" : `/reports/${item.employee_id}`
              }
              className="font-medium text-brand-700 hover:underline"
            >
              {item.asset ?? item.name}
            </Link>
            {item.status === "archived" && (
              <span className="rounded-full bg-slate-100 px-2 text-xs leading-5 text-slate-600">
                {t("مؤرشف")}
              </span>
            )}
            <span className="text-slate-600">{describe(item)}</span>
          </li>
        ))}
      </ul>
      {alert.items.length > SHOWN && (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="mt-2 text-sm font-medium text-brand-700 hover:underline"
        >
          {all ? t("عرض أقل") : t("عرض الكل ({n})", { n: alert.items.length })}
        </button>
      )}
      {hidden > 0 && all && (
        <p className="mt-1 text-sm text-slate-600">{t("و {n} آخرين", { n: hidden })}</p>
      )}
    </li>
  );
};

// Things to check before payday, found from the recorded data (unpaid old work, odd entries, big debts ...).
const Alerts = () => {
  const [state, setState] = useState({ status: "loading", alerts: [] });

  const load = useCallback((signal) => {
    setState({ status: "loading", alerts: [] });
    axios
      .get("/alerts", { params: { today: todayIso() }, signal })
      .then(({ data }) => {
        if (!Array.isArray(data?.alerts)) throw new Error("bad shape");
        setState({ status: "ok", alerts: data.alerts.filter((a) => DETAIL[a.type]) });
      })
      .catch((err) => {
        if (err.code === "ERR_CANCELED") return;
        setState({ status: "error", alerts: [] });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const { status, alerts } = state;
  if (status === "loading") return null; // the summary below is the first thing to read; no placeholder jumps

  return (
    <section aria-labelledby="alerts-title" className="mb-6">
      <h2 id="alerts-title" className="mb-3 text-lg font-semibold text-brand-800">
        {t("تنبيهات قبل يوم الدفع")}
      </h2>
      {status === "error" && (
        <div role="alert" className="card flex flex-wrap items-center gap-3 p-4 text-slate-700">
          <span>{t("تعذر تحميل التنبيهات.")}</span>
          <button
            type="button"
            onClick={() => load()}
            className="rounded-lg border border-slate-300 bg-card px-3 py-1.5 font-medium hover:bg-slate-100"
          >
            {t("إعادة المحاولة")}
          </button>
        </div>
      )}
      {status === "ok" && alerts.length === 0 && (
        <p className="card flex items-center gap-2 p-4 text-slate-700">
          <LuCircleCheck aria-hidden="true" className="text-emerald-600" />
          {t("لا توجد تنبيهات. كل شيء يبدو سليماً.")}
        </p>
      )}
      {status === "ok" && alerts.length > 0 && (
        <ul className="grid gap-3 lg:grid-cols-2">
          {alerts.map((alert) => (
            <AlertCard key={alert.type} alert={alert} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default Alerts;
